import { useState } from 'react';
import {
    WhitelabelSettingsInfo,
    CreateWhitelabelSettingsRequest,
    UpdateWhitelabelSettingsRequest,
} from '../../lib/types';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';

export default function useWhitelabelSettings() {
    const [error, setError] = useState<string | null>(null);
    const authenticatedApi = useAuthenticatedApi();

    /**
     * Get all whitelabel settings for an organization
     */
    const getWhitelabelSettingsByOrgId = async (
        orgId: number,
    ): Promise<WhitelabelSettingsInfo[]> => {
        try {
            const response = await authenticatedApi.get(
                `/${orgId}/whitelabel/org/${orgId}`,
            );
            return response.data.data;
        } catch (err: any) {
            setError(
                err.response?.data?.message ||
                    'Failed to get whitelabel settings',
            );
            throw err;
        }
    };

    /**
     * Get a specific whitelabel setting by ID
     */
    const getWhitelabelSettings = async (
        orgId: number,
        id: number,
    ): Promise<WhitelabelSettingsInfo> => {
        try {
            const response = await authenticatedApi.get(
                `/${orgId}/whitelabel/${id}`,
            );
            return response.data.data;
        } catch (err: any) {
            setError(
                err.response?.data?.message ||
                    'Failed to get whitelabel settings',
            );
            throw err;
        }
    };

    /**
     * Create a new whitelabel setting
     */
    const createWhitelabelSettings = async (
        orgId: number,
        data: CreateWhitelabelSettingsRequest,
    ): Promise<WhitelabelSettingsInfo> => {
        try {
            const response = await authenticatedApi.post(
                `/${orgId}/whitelabel`,
                data,
            );
            return response.data.data;
        } catch (err: any) {
            setError(
                err.response?.data?.message ||
                    'Failed to create whitelabel settings',
            );
            throw err;
        }
    };

    /**
     * Update an existing whitelabel setting
     */
    const updateWhitelabelSettings = async (
        orgId: number,
        id: number,
        data: UpdateWhitelabelSettingsRequest,
    ): Promise<WhitelabelSettingsInfo> => {
        try {
            const response = await authenticatedApi.put(
                `/${orgId}/whitelabel/${id}`,
                data,
            );
            return response.data.data;
        } catch (err: any) {
            setError(
                err.response?.data?.message ||
                    'Failed to update whitelabel settings',
            );
            throw err;
        }
    };

    const deleteWhitelabelSettings = async (
        orgId: number,
        id: number,
    ): Promise<boolean> => {
        try {
            const response = await authenticatedApi.delete(
                `/${orgId}/whitelabel/${id}`,
            );
            return response.data.success;
        } catch (err: any) {
            setError(
                err.response?.data?.message ||
                    'Failed to delete whitelabel settings',
            );
            throw err;
        }
    };

    const getOrgDomain = async (orgId: number): Promise<string> => {
        try {
            const response = await authenticatedApi.get(
                `/${orgId}/whitelabel/domain/${orgId}`,
            );
            return response.data.data.domain;
        } catch (err: any) {
            setError(
                err.response?.data?.message ||
                    'Failed to get organization domain',
            );
            throw err;
        }
    };

    return {
        error,
        setError,
        getWhitelabelSettingsByOrgId,
        getWhitelabelSettings,
        createWhitelabelSettings,
        updateWhitelabelSettings,
        deleteWhitelabelSettings,
        getOrgDomain,
    };
}
